// Routes.
//
// Every URL in the ticket app is keyed on `public_ref`, the customer-visible ticket
// number — not `tickets.id` — so references carried over from Zendesk still resolve
// after the migration (docs/API.md §3.2).

import { Route, Navigate, useParams } from 'react-router';

import AppLayout from './layout/AppLayout.jsx';
import { assertRoutesCoverSections } from './layout/nav-items.js';
import { ROUTE_PATHS } from './route-paths.js';
import { supportPath } from './support-path.js';
import TicketList from './pages/tickets/List.jsx';
import TicketDetail from './pages/tickets/Detail.jsx';
import TicketNew from './pages/tickets/New.jsx';
import NewDraft from './pages/tickets/NewDraft.jsx';
import TeamDrafts from './pages/tickets/TeamDrafts.jsx';
import Search from './pages/Search.jsx';
import AdminConsole from './pages/admin/Console.jsx';
import AdminSchedules from './pages/admin/Schedules.jsx';
import AdminSettings from './pages/admin/Settings.jsx';
import AdminIntegrations from './pages/admin/Integrations.jsx';
import IntegrationEdit from './pages/admin/IntegrationEdit.jsx';
import AdminResourceList from './pages/admin/ResourceList.jsx';
import AdminResourceEdit from './pages/admin/ResourceEdit.jsx';
import CustomerList from './pages/customers/List.jsx';
import CustomerDetail from './pages/customers/Detail.jsx';
import SendLists from './pages/customers/SendLists.jsx';
import Reports from './pages/reports/Reports.jsx';
import NotFound from './pages/NotFound.jsx';
import { isDraftId } from './components/drafts/draft-model.js';

// Every path the tree below renders is declared once, in route-paths.js, so the nav can be
// held to it: `assertRoutesCoverSections` throws at import time — at boot, in the
// browser console — if a section in nav-items.js has no route. That is the sync
// the two hardcoded lists never had.
assertRoutesCoverSections(ROUTE_PATHS);

// The create screen, keyed by its draft: opening another draft's tab mounts a fresh form
// rather than carrying one draft's screen state into the next. Only one form is ever
// mounted (the spec's "One form at a time"). An id that is not a GUID is not a draft (the
// server answers 404 to it, and no save could ever create it), so it is a missing page.
function DraftScreen() {
  const { draftId } = useParams();
  if (!isDraftId(draftId)) return <NotFound />;
  return <TicketNew key={draftId} />;
}

/**
 * Support's pages, handed to the platform shell by support-module/entry.jsx. RELATIVE to
 * Support's basePath (/support, support-path.js): the shell mounts them at `/support/*`,
 * so `tickets` here is /support/tickets. The shell owns "/".
 */
export const supportRoutes = (
  <Route element={<AppLayout />}>
    {/* /support itself lands on the queue: Support's own index, not the platform's "/". */}
    <Route index element={<Navigate to={supportPath('/tickets')} replace />} />

    <Route path="tickets" element={<TicketList />} />
    {/* `new` before `:publicRef` so it is not read as a ticket number.
        public_ref is numeric, but the router does not know that. It is not a
        screen: it mints a draft and replaces itself with that draft's address
        (pages/tickets/NewDraft.jsx), so every link to it keeps working. */}
    <Route path="tickets/new" element={<NewDraft />} />
    {/* `team` before `:draftId`: a literal segment outranks a param in
        react-router anyway, and no draft id (a GUID) is the string `team`; the
        order is stated so the reason is on the page. */}
    <Route path="tickets/drafts/team" element={<TeamDrafts />} />
    {/* The create screen, bound to one draft. */}
    <Route path="tickets/drafts/:draftId" element={<DraftScreen />} />
    <Route path="tickets/:publicRef" element={<TicketDetail />} />

    {/* Not a nav section: the top bar's search box is the way in, or its search
        icon where the bar has no room for a box (layout/AppLayout.jsx). */}
    <Route path="search" element={<Search />} />

    <Route path="customers" element={<CustomerList />} />
    {/* Static before dynamic, as with tickets/new: react-router ranks a
        literal segment above a param, but the order is stated anyway so the
        reason is on the page rather than in the router docs. */}
    <Route path="customers/send-lists" element={<SendLists />} />
    <Route path="customers/:id" element={<CustomerDetail />} />

    {/* Both paths render the same component; it reads :reportId to pick
        which report is open, so the bare reports path lands on the default. */}
    <Route path="reports" element={<Reports />} />
    <Route path="reports/:reportId" element={<Reports />} />

    {/* Deep links need no server change: src/server.js:435 mounts a
        catch-all GET after express.static and serves index.html for any
        unmatched path. The API is what refuses a non-lead, not this. */}
    <Route path="admin" element={<AdminConsole />} />
    {/* Static, so react-router ranks it above `admin/:resource`
        regardless of declaration order. The schedule grid is one of the
        three screens design §7 says cannot be generic, so it does not go
        through the descriptor framework — and there is deliberately no
        `agent_schedules` entry in DESCRIPTORS for `admin/:resource` to
        find. */}
    <Route path="admin/schedules" element={<AdminSchedules />} />
    {/* Ahead of `admin/:resource` for the same reason schedules is: a literal
        segment outranks a param in react-router, and stating the order keeps the
        reason on the page. There is deliberately no `app_settings` descriptor —
        the generic routes would let a caller insert any key it liked, which is
        exactly what the settings registry exists to prevent. */}
    <Route path="admin/settings" element={<AdminSettings />} />
    {/* Ahead of `admin/:resource` for the same reason schedules and settings
        are: a literal segment outranks a param in react-router, and stating the
        order keeps the reason on the page rather than in the router docs. There
        is deliberately no `integrations` descriptor — this screen writes a sealed
        credential blob and calls `/test` and `/api-clients`, none of which the
        generic row-id-keyed routes can express.

        `admin/integrations/new?kind=…` is matched by the `:id` route and
        IntegrationEdit branches on `id === 'new'`, exactly as ResourceEdit does.
        The kind rides in the query string because it is not a resource path: it
        is which FORM to draw, and the created row's id is what the URL becomes. */}
    <Route path="admin/integrations" element={<AdminIntegrations />} />
    <Route path="admin/integrations/:id" element={<IntegrationEdit />} />
    {/* `admin/:resource/new` is matched by the `:id` route and
        ResourceEdit branches on `id === 'new'`. The opposite of what
        tickets/new needs: there, `new` had to be declared FIRST so it
        was not read as a public_ref. Here the segment is a descriptor
        id, ids are the only other thing that appears in this position,
        and no descriptor is named `new`. */}
    <Route path="admin/:resource" element={<AdminResourceList />} />
    <Route path="admin/:resource/:id" element={<AdminResourceEdit />} />

    {/* An unknown address INSIDE /support is Support's not-found; outside every module
        it is the shell's (web/shell/ShellNotFound.jsx). */}
    <Route path="*" element={<NotFound />} />
  </Route>
);
